/**
 * Reports — two sections, because they answer different kinds of question.
 *
 * TIME mirrors the standalone tracker's Insights screen: a period, what it came
 * to, and where it went. PROJECTS is cumulative — budget burn-down, retainer
 * position — and is where the report types still to come will live.
 *
 * Deliberately separate from the Time view: that one is for logging and
 * correcting today's entries, this one for answering a question about a period
 * ("what do I put on the September invoice for Riverside?").
 */

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useChrome } from "@/context/ChromeContext";
import { HeaderTabs } from "@/components/HeaderTabs";
import { useWeeklyReview } from "@/lib/theme";
import { useIsMobile } from "@/lib/useMediaQuery";
import { WeeklyReview } from "@/views/WeeklyReview";

import { ProjectsReport } from "./reports/ProjectsReport";
import { TimeReport } from "./reports/TimeReport";

type Section = "time" | "projects" | "review";

export function Reports() {
  const mobile = useIsMobile();
  const [weeklyReview] = useWeeklyReview();
  const SECTIONS: [Section, string][] = [
    ["time", "Time"], ["projects", "Projects"],
    ...(weeklyReview === "on" ? [["review", "Weekly review"] as [Section, string]] : []),
  ];

  // Always opens on Time, unless a link asks for a tab ("/reports?tab=review",
  // which is where the old /review address now lands). The query is consumed
  // so a later tab change is not overridden by it.
  const [params, setParams] = useSearchParams();
  const asked = params.get("tab") as Section | null;
  const [picked, setSection] = useState<Section>(
    asked && SECTIONS.some(([k]) => k === asked) ? asked : "time",
  );
  useEffect(() => {
    if (params.get("tab")) setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Switched off in Settings while on the tab: fall back rather than show nothing.
  const section: Section = SECTIONS.some(([k]) => k === picked) ? picked : "time";

  const tabsNode = <HeaderTabs value={section} options={SECTIONS} onChange={setSection} />;

  useChrome({
    title: "Reports",
    subtitle: section === "review" ? "Five steps through the week" : "Where the time went",
    tabs: tabsNode,
  }, [section]);

  return (
    <div style={{ padding: mobile ? "16px 16px 90px" : "20px 36px 90px", maxWidth: 1000 }}>
      {section === "time" ? <TimeReport /> : section === "projects" ? <ProjectsReport /> : <WeeklyReview />}
    </div>
  );
}
